/**
 * Same-origin UN8 API client. credentials: include for HttpOnly cookie.
 * Local stores stay as fallback when /api/health is unreachable.
 */

const API_BASE_KEY = "un8.api.base.v1";

export function getApiBase(): string {
  try {
    const stored = localStorage.getItem(API_BASE_KEY);
    if (stored) return stored.replace(/\/+$/, "");
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined" && (window as Window & { __RR_API__?: string }).__RR_API__) {
    return String((window as Window & { __RR_API__?: string }).__RR_API__).replace(/\/+$/, "");
  }
  // Same origin when hosted on Pages; local static serve needs pages-dev proxy.
  return "";
}

export function setApiBase(base: string): void {
  try {
    localStorage.setItem(API_BASE_KEY, base.replace(/\/+$/, ""));
  } catch {
    /* ignore */
  }
}

export type ApiResult<T> =
  | { ok: true; data: T; status: number }
  | { ok: false; error: string; status: number };

let healthCache: { ok: boolean; at: number } | null = null;

export async function apiHealth(force = false): Promise<boolean> {
  const now = Date.now();
  if (!force && healthCache && now - healthCache.at < 15_000) return healthCache.ok;
  try {
    const res = await fetch(`${getApiBase()}/api/health`, {
      method: "GET",
      credentials: "include",
    });
    const ok = res.ok;
    healthCache = { ok, at: now };
    return ok;
  } catch {
    healthCache = { ok: false, at: now };
    return false;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<ApiResult<T>> {
  const url = `${getApiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  try {
    const headers = new Headers(init.headers || {});
    if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    const res = await fetch(url, {
      ...init,
      headers,
      credentials: "include",
    });
    let data: unknown = null;
    const text = await res.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!res.ok) {
      const err =
        data && typeof data === "object" && data !== null && "error" in data
          ? String((data as { error: unknown }).error)
          : `Request failed (${res.status})`;
      return { ok: false, error: err, status: res.status };
    }
    return { ok: true, data: data as T, status: res.status };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Network error",
      status: 0,
    };
  }
}

export async function apiJson<T = unknown>(
  path: string,
  method: string,
  body?: unknown
): Promise<ApiResult<T>> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}
